import * as React from 'react';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import { FormContextProvider, HistoryContextProvider } from 'Containers/FormContextHandler';
import FormEditingHandler from 'Containers/FormEditingHandler';
import {
  docType,
  getHeaderId,
  mapPatientDoesNotWantRespiratorySupportTherapyEndDates,
  patientHasRespiratoryFailureDiagnosis,
  patientHasSleepApneaDiagnosis,
} from '../utils';
import Form from './Form';
import PatientDoesNotWantRespiratorySupportTherapyHistory from './HistoryRowData/PatientDoesNotWantRespiratorySupportTherapy';
import OxygenTherapyHistory from './HistoryRowData/OxygenTherapy';
import HFNCTherapyHistory from './HistoryRowData/HFNCTherapy';
import PAPTherapyHistory from './HistoryRowData/PAPTherapy';
import MADTherapyHistory from './HistoryRowData/MADTherapy';
import SurgicalTreatmentHistory from './HistoryRowData/SurgicalTreatment';
import OtherTreatmentHistory from './HistoryRowData/OtherTreatment';
import type { TCreationPromiseType } from '../../../store/documents/actions';
import { actions } from '../../../store/documents/actions';
import { assertCapabilities, useAppDispatch, useAppSelector } from 'Store/index';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import Icon from 'Components/_NewElements/Icon';
import { fm } from 'Components/FormatMessage';
import type { ICapabilityContextProps } from 'Containers/CapabilityHandler';
import { withCapabilities } from 'Containers/CapabilityHandler';
import type { IThemedDialog } from 'Components/_NewElements/Dialog';
import Dialog from 'Components/_NewElements/Dialog';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';
import { treatmentDocTypes } from './definitions';
import type { RelevantDocumentType } from './definitions';

export const PAPTherapyContext = React.createContext<IPAPTherapyContext>({
  resMedIntegrationAvailable: false,
  resMedDialogOpen: false,
  setResMedDialogOpen: () => '',
  openInformationDialog: () => '',
});

interface IPAPTherapyContext {
  resMedIntegrationAvailable: boolean;
  resMedDialogOpen: boolean;
  setResMedDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openInformationDialog: (text: string | React.JSX.Element, hasTimeout?: boolean | number) => void;
}

const TreatmentHistory = ({ documents }: { documents: IOwnProps['documents'] }): React.JSX.Element => {
  const diagnosisDocs = filterByDocumentType(documents, 'diagnosis');

  const noRelevantDiagnosis =
    !patientHasRespiratoryFailureDiagnosis(diagnosisDocs) && !patientHasSleepApneaDiagnosis(diagnosisDocs);
  return (
    <React.Fragment>
      {/** Potilas ei halua hengitystukihoitoa */}
      <PatientDoesNotWantRespiratorySupportTherapyHistory />

      {/** Happihoito */}
      <OxygenTherapyHistory />

      {/** HFNC-hoito */}
      <HFNCTherapyHistory />

      {/** PAP-hoito */}
      <PAPTherapyHistory />

      {/** Uniapneakiskohoito */}
      {(patientHasSleepApneaDiagnosis(diagnosisDocs) || noRelevantDiagnosis) && <MADTherapyHistory />}

      {/** Kirurginen hoito */}
      {(patientHasSleepApneaDiagnosis(diagnosisDocs) || noRelevantDiagnosis) && <SurgicalTreatmentHistory />}

      {/** Muu hoito */}
      <OtherTreatmentHistory />
    </React.Fragment>
  );
};

const DEFAULT_CAPABILITY_GROUPS: IGroupACLMapping = {};
const Treatment = ({ documents, capabilityGroups = DEFAULT_CAPABILITY_GROUPS }: IOwnProps): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const createCommit = async (id: string): Promise<TCreationPromiseType> =>
    actions.createCommit({ id, name: 'patientDoesNotWantRespiratorySupportTherapy' })(dispatch);

  const closeCommitWithData = (id: string, data: any & IControlProps) =>
    actions.closeCommit({ id, name: 'patientDoesNotWantRespiratorySupportTherapy' }, data)(dispatch);

  const isEditing = documents.some(d => d._editing === true);

  React.useEffect(() => {
    const docsWithUpdatedEndDates = mapPatientDoesNotWantRespiratorySupportTherapyEndDates(documents);
    // PDNWRST endDates are only updated if they don't exist, not if there isn't a matching treatment start/decision date
    if (!isEditing) {
      docsWithUpdatedEndDates.forEach(d => {
        createCommit(d._id)
          .then(() => {
            void closeCommitWithData(d._id, d);
          })
          .catch(() => {
            return;
          });
      });
    }
  }, [isEditing]);

  const visitReasonRetro = useAppSelector((s: IState) => s.session.data?.visitreason === 'retrospectiveDataTransfer');

  const [informationDialogSettings, setInformationDialogSettings] = React.useState<IThemedDialog | null>(null);

  const openInformationDialog = (text: string | React.JSX.Element, hasTimeout: boolean | number = true) => {
    setInformationDialogSettings({
      open: true,
      children: text,
      onClose: () => setInformationDialogSettings(null),
      closeTimeout: typeof hasTimeout === 'number' ? hasTimeout : hasTimeout ? 3000 : undefined,
    });
  };

  const resMedIntegrationAvailable = assertCapabilities(['RESMED_AVX_INTEGRATION'], capabilityGroups);
  const resMedIntegrationEnabled = useAppSelector((s: IState) => {
    const details = s.session.patientIntegrationDetails;
    if (details === undefined) return undefined;
    return details.resMed !== null;
  });
  const [resMedDialogOpen, setResMedDialogOpen] = React.useState<boolean>(false);

  const resMedContext = {
    resMedIntegrationAvailable,
    resMedDialogOpen,
    setResMedDialogOpen,
    openInformationDialog,
  };

  return (
    <FormEditingHandler name="treatment" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => {
        const treatmentDocs = filterDocumentsByType(documents, treatmentDocTypes);
        const editingDocument = editing ? treatmentDocs.find(d => d._id === editing) : undefined;
        const viewingDocument = view?.viewing ? treatmentDocs.find(d => d._id === view.viewing) : undefined;

        return (
          <DocumentWrapper>
            <DocumentHeader
              name="treatment"
              headerId={getHeaderId(documents, editing, view?.viewing)}
              editing={editing}
              editButtons={<div />}
              infoText={
                resMedIntegrationAvailable && docType(documents, editing || view?.viewing) === 'papTherapy' ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ fontWeight: '600' }}>
                      {fm('treatment.papTherapy.resMedIntegration')}{' '}
                      <span style={{ textTransform: 'lowercase' }}>
                        {fm(`treatment.papTherapy.resMed${resMedIntegrationEnabled ? 'InUse' : 'NotInUse'}`)}
                      </span>
                    </div>
                    <ActionButtonRounded
                      text={
                        <Container alignItems="center" justifyContent="space-between">
                          <Item>
                            {resMedIntegrationEnabled ? <Icon icon="mergeType" /> : <Icon icon="sqicon" size={3.1} />}
                          </Item>
                          <Item xs={true}>
                            {fm(`treatment.papTherapy.${resMedIntegrationEnabled ? 'editResMed' : 'connectResMed'}`)}
                          </Item>
                        </Container>
                      }
                      width={26}
                      height={4}
                      fontSize={16}
                      onClick={() => setResMedDialogOpen(true)}
                      disabled={typeof resMedIntegrationEnabled !== 'boolean' || !!view?.viewing}
                    />
                  </div>
                ) : undefined
              }
              headerWidth={12}
            />
            <PAPTherapyContext.Provider value={resMedContext}>
              {/** Editing */}
              {editingDocument ? (
                <FormContextProvider context={{ formData, documents, editing }}>
                  <Form document={editingDocument} />
                </FormContextProvider>
              ) : null}
              {/** Viewing */}
              {viewingDocument ? (
                <FormContextProvider context={{ formData, documents, view }}>
                  <Form document={viewingDocument} />
                </FormContextProvider>
              ) : null}
              {/** History */}
              {!editing && !view?.viewing ? (
                <HistoryContextProvider context={{ documents, startEdit, view, additionalData: { visitReasonRetro } }}>
                  <TreatmentHistory documents={documents} />
                </HistoryContextProvider>
              ) : null}
            </PAPTherapyContext.Provider>
            {informationDialogSettings ? (
              <Dialog
                open={informationDialogSettings.open}
                type="info"
                onClose={informationDialogSettings.onClose}
                closeTimeout={informationDialogSettings.closeTimeout}
              >
                {informationDialogSettings.children}
              </Dialog>
            ) : null}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IOwnProps extends ICapabilityContextProps {
  documents: IDocumentWithTypeV2<RelevantDocumentType>[];
}

export default withCapabilities(Treatment);
